import React, { useEffect, useState } from "react";
import axios from "axios";
import { Autocomplete, TextField, Chip } from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { Calendar, Clock, Users2, User, Repeat } from "lucide-react";
import styles from "./hiralityMeetings.module.scss";
import Datepicker from "components/minor/date-picker/date-picker";
import { useTheme } from "context/ThemeContext";
import { apiBaseUrl } from "../../../../../../services/config";
import { MEETING_TYPES } from "./meetingTypes";

const API = apiBaseUrl;

const TIME_OPTIONS = [
  "1:00","1:30","2:00","2:30","3:00","3:30","4:00","4:30",
  "5:00","5:30","6:00","6:30","7:00","7:30","8:00","8:30",
  "9:00","9:30","10:00","10:30","11:00","11:30","12:00","12:30",
];
const DURATIONS = [15, 30, 45, 60, 90, 120];
const RECURRENCE_OPTIONS = [
  { value: "daily",    label: "Daily" },
  { value: "weekly",   label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly",  label: "Monthly" },
];

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Meeting title is required"),
  date:  Yup.string().required("Date is required"),
  time:  Yup.string().required("Time is required"),
});

const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || ""}`,
});

// The shared Datepicker (MUI DatePicker under the hood) hands back a raw JS
// Date object via onChange, not a string — left as-is, axios's JSON
// serialization turns it into a full ISO datetime ("2026-08-19T05:00:00.000Z"),
// which the backend's plain YYYY-MM-DD parser rejects. Normalize using local
// date parts (not toISOString(), which can shift the day across a UTC
// boundary) so "today" stays "today" regardless of timezone.
const toDateStr = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10); // already YYYY-MM-DD (edit mode)
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ── Parses an existing meeting_time like "09:30 AM" into {time,timetype} ──
const parseExistingTime = (meetingData) => {
  if (meetingData?.meeting_time) {
    const m = meetingData.meeting_time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m) return { time: `${Number(m[1])}:${m[2]}`, timetype: m[3].toUpperCase() };
  }
  return { time: "9:00", timetype: "AM" };
};

const MeetingForm = ({ meetingData, header, onCloseDialog, onSaved }) => {
  const { T } = useTheme();
  const [employees, setEmployees] = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [apiError,  setApiError]  = useState("");

  useEffect(() => {
    axios.get(`${API}/hirality-meetings/employees`, { headers: authHeader() })
      .then(r => setEmployees(r.data || []))
      .catch(e => console.error("Failed to load employees:", e));
  }, []);

  const { time: initTime, timetype: initAMPM } = parseExistingTime(meetingData);
  const initHost = meetingData?.host_employee_id
    ? employees.find(e => e.id === meetingData.host_employee_id) || null
    : null;

  const handleSubmit = async (values) => {
    setSaving(true); setApiError("");
    try {
      const [h, m] = values.time.split(":").map(Number);
      // Stored as a plain 12-hour display string — the backend doesn't
      // interpret meeting_time, it just carries it through for display.
      const timeStr = `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")} ${values.timetype}`;

      const payload = {
        title:             values.title,
        description:       values.description || null,
        host_employee_id:  values.host ? Number(values.host.id) : null,
        participants:      values.participants.map(p => ({
          name:  p.name  || p.title || (typeof p === "string" ? p : ""),
          email: p.email || (typeof p === "string" ? p : ""),
        })),
        meeting_date:      toDateStr(values.date),
        meeting_time:      timeStr,
        duration_minutes:  Number(values.duration) || 30,
        meeting_type:      values.meetingType,
      };

      // Recurring setup only applies when scheduling a fresh series — not
      // meaningful for editing a single existing occurrence.
      if (!meetingData?.id && values.isRecurring) {
        payload.is_recurring = true;
        payload.recurrence_frequency = values.recurrenceFrequency;
        payload.recurrence_weekdays_only = values.recurrenceFrequency === "daily" && values.recurrenceDailyMode === "weekdays";
        payload.recurrence_end_type = values.recurrenceEndType;
        if (values.recurrenceEndType === "until_date") {
          payload.recurrence_until_date = toDateStr(values.recurrenceUntilDate);
        } else {
          payload.recurrence_count = Number(values.recurrenceCount) || 4;
        }
      }

      let res;
      if (meetingData?.id) {
        res = await axios.put(`${API}/hirality-meetings/${meetingData.id}`, payload, { headers: authHeader() });
      } else {
        res = await axios.post(`${API}/hirality-meetings/`, payload, { headers: authHeader() });
      }
      // Caller decides what happens next — e.g. the "Schedule Meeting" modal
      // shows the join link instead of closing immediately, while "Edit"
      // just closes. So this only reports success; it doesn't close itself.
      onSaved?.(res.data);
    } catch (e) {
      setApiError(e.response?.data?.detail || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        title:       meetingData?.title       || "",
        description: meetingData?.description || "",
        date:        meetingData?.meeting_date || "",
        time:        initTime,
        timetype:    initAMPM,
        duration:    meetingData?.duration_minutes || 30,
        host:        initHost,
        participants: meetingData?.participants || [],
        meetingType: meetingData?.meeting_type || "meeting",
        isRecurring:            false,
        recurrenceFrequency:    "weekly",
        recurrenceDailyMode:    "everyday",   // everyday | weekdays
        recurrenceEndType:      "count",      // count | until_date
        recurrenceCount:        4,
        recurrenceUntilDate:    "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Meeting Title *</label>
            <Field name="title" className={styles.formInput} placeholder="e.g. Sprint Planning" />
            {touched.title && errors.title && <p className={styles.formError}>{errors.title}</p>}
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Meeting Type</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {MEETING_TYPES.map(({ key, label, icon: Icon, color }) => {
                const active = values.meetingType === key;
                const c = T[color];
                return (
                  <button key={key} type="button" onClick={() => setFieldValue("meetingType", key)}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8,
                      fontSize: 12.5, fontWeight: 600, fontFamily: "Outfit,sans-serif", cursor: "pointer",
                      border: `1px solid ${active ? c : T.border}`,
                      background: active ? `${c}18` : "transparent",
                      color: active ? c : T.muted }}>
                    <Icon size={13} /> {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Description</label>
            <Field as="textarea" name="description" rows={2} className={styles.formInput}
              style={{ resize: "vertical" }} placeholder="What's this meeting about?" />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}><Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }}/>Date *</label>
            <Datepicker name="date" label="Date" value={values.date || ""} onChange={(v) => setFieldValue("date", v)} />
            {touched.date && errors.date && <p className={styles.formError}>{errors.date}</p>}
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}><Clock size={11} style={{ verticalAlign: "middle", marginRight: 4 }}/>Time *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <Field as="select" name="time" className={styles.formInput}>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </Field>
              <Field as="select" name="timetype" className={styles.formInput} style={{ maxWidth: 90 }}>
                <option>AM</option><option>PM</option>
              </Field>
              <Field as="select" name="duration" className={styles.formInput} style={{ maxWidth: 130 }}>
                {DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
              </Field>
            </div>
          </div>

          {!meetingData?.id && (
            <div className={styles.formRow}>
              <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12.5, color: T.text, fontWeight: 600 }}>
                <input type="checkbox" checked={values.isRecurring} onChange={(e) => setFieldValue("isRecurring", e.target.checked)} />
                <Repeat size={12} style={{ color: T.cyan }} /> Repeat this meeting
              </label>

              {values.isRecurring && (
                <div style={{ marginTop: 12, padding: 14, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Frequency */}
                  <div>
                    <label className={styles.formLabel}>Repeat every</label>
                    <Field as="select" name="recurrenceFrequency" className={styles.formInput}>
                      {RECURRENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Field>
                  </div>

                  {/* Daily: every day vs weekdays only */}
                  {values.recurrenceFrequency === "daily" && (
                    <div>
                      <label className={styles.formLabel}>On</label>
                      <div style={{ display: "flex", gap: 16 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                          <input type="radio" name="recurrenceDailyMode" checked={values.recurrenceDailyMode === "everyday"}
                            onChange={() => setFieldValue("recurrenceDailyMode", "everyday")} />
                          Every day
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                          <input type="radio" name="recurrenceDailyMode" checked={values.recurrenceDailyMode === "weekdays"}
                            onChange={() => setFieldValue("recurrenceDailyMode", "weekdays")} />
                          Weekdays only (Mon–Fri)
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Ends */}
                  <div>
                    <label className={styles.formLabel}>Ends</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                        <input type="radio" name="recurrenceEndType" checked={values.recurrenceEndType === "count"}
                          onChange={() => setFieldValue("recurrenceEndType", "count")} />
                        After
                        <Field type="number" name="recurrenceCount" min={2} max={52} className={styles.formInput}
                          style={{ width: 64 }} disabled={values.recurrenceEndType !== "count"} />
                        occurrences
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                        <input type="radio" name="recurrenceEndType" checked={values.recurrenceEndType === "until_date"}
                          onChange={() => setFieldValue("recurrenceEndType", "until_date")} />
                        On date
                        <div style={{ flex: 1, opacity: values.recurrenceEndType === "until_date" ? 1 : 0.5,
                          pointerEvents: values.recurrenceEndType === "until_date" ? "auto" : "none" }}>
                          <Datepicker name="recurrenceUntilDate" label="" value={values.recurrenceUntilDate || ""}
                            onChange={(v) => setFieldValue("recurrenceUntilDate", v)} />
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={styles.formRow}>
            <label className={styles.formLabel}><User size={11} style={{ verticalAlign: "middle", marginRight: 4 }}/>Host</label>
            <Autocomplete
              options={employees}
              getOptionLabel={(o) => o.name || ""}
              value={values.host}
              onChange={(_, v) => setFieldValue("host", v)}
              renderInput={(params) => <TextField {...params} placeholder="Defaults to you" />}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}><Users2 size={11} style={{ verticalAlign: "middle", marginRight: 4 }}/>Participants</label>
            <Autocomplete
              multiple
              freeSolo
              options={employees}
              getOptionLabel={(o) => (typeof o === "string" ? o : (o.name || o.email || ""))}
              value={values.participants}
              onChange={(_, v) => setFieldValue("participants", v)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined"
                    label={typeof option === "string" ? option : (option.name || option.email)}
                    {...getTagProps({ index })} key={index} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Add employees, or type an external email…" />
              )}
            />
          </div>

          {apiError && (
            <div style={{ margin: "8px 0", padding: "8px 12px", background: `${T.red}12`,
              border: `1px solid ${T.red}30`, borderRadius: 7, fontSize: 12.5, color: T.red }}>
              ⚠️ {apiError}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
            {onCloseDialog && (
              <button type="button" className={styles.btnSecondary} onClick={onCloseDialog}>Cancel</button>
            )}
            <button type="submit" className={styles.btnPrimary} disabled={saving}>
              {saving ? "Saving…" : (meetingData?.id ? "Update Meeting" : "Schedule Meeting")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MeetingForm;
